import {
  FolderOpen, TrendingUp, FileText, Clock, CheckCircle2,
  IndianRupee, Users, ClipboardList, XCircle, AlertTriangle, HardHat, Hourglass,
} from 'lucide-react';
import { DASHBOARD_WIDGET_BY_KEY } from '@shared/dashboard-contract';
import { DECISION_CARD_METRICS, getDashboardMetric, metricDrillPath } from '@shared/dashboard-metrics';
import { formatNumber, formatCurrency } from '@/lib/utils';
import { safeNum, type StatCardDef } from './shared';
import * as W from './widgets';

/**
 * Central Dashboard display registry (web).
 *
 * Mirrors the worker's widget registry key-for-key. The worker decides which
 * widgets a user may receive (permissions + scope); this registry decides how
 * an authorized widget is rendered, where it links, and its empty state.
 * The page NEVER renders a widget key that is not present in the `/stats`
 * response, and it defensively checks `user.permissions` via the shared widget
 * definitions before rendering.
 */

type AnyData = any;
export type DashboardWidgetVariant = 'stat' | 'chart' | 'list';

// Drill-down for a count card: the shared metric registry is the single source
// of truth for the row set behind the card, so the destination list page
// reproduces exactly the rows the card counted.
function metricTo(key: string): string {
  return metricDrillPath(getDashboardMetric(key)!);
}

// Drill-down for analysis list cards: keep the metric snapshot but also mirror
// the Organization Analysis filter bar (project + date range) so the list page
// is scoped to the range the charts were computed for.
function metricLink(key: string, urls: W.DashboardUrls): string {
  const base = metricDrillPath(getDashboardMetric(key)!);
  const f = urls.filters;
  if (!f || (!f.projectId && !f.startDate && !f.endDate)) return base;
  const p = new URLSearchParams(base.split('?')[1]);
  if (f.projectId) p.set('projectId', f.projectId);
  if (f.startDate) p.set('dateFrom', f.startDate);
  if (f.endDate) p.set('dateTo', f.endDate);
  return `${base.split('?')[0]}?${p.toString()}`;
}

export interface DashboardStatRenderer {
  variant: 'stat';
  stat: (data: AnyData, urls: W.DashboardUrls, admin: boolean) => StatCardDef;
}

export interface DashboardBlockRenderer {
  variant: 'chart' | 'list';
  render: (data: AnyData, urls: W.DashboardUrls, admin: boolean) => React.ReactNode;
}

export type DashboardWidgetRenderer = DashboardStatRenderer | DashboardBlockRenderer;

const num = (d: AnyData) => formatNumber(safeNum(d?.value));
const cur = (d: AnyData) => formatCurrency(safeNum(d?.value));
const label = (key: string) => DASHBOARD_WIDGET_BY_KEY[key]?.label ?? key;
const empty = (key: string) => DASHBOARD_WIDGET_BY_KEY[key]?.emptyText ?? 'No data in this range.';

interface StatConfig {
  icon: React.ReactNode;
  accent: StatCardDef['accent'];
  value?: (d: AnyData) => string;
  to?: (urls: W.DashboardUrls, admin: boolean) => string | undefined;
  /** Combined cards render a popover with per-entity drill-down links. */
  dialog?: (d: AnyData) => StatCardDef['dialog'];
}

// "by Me" decision cards open a popover with per-entity (Requirements / Actual
// Labour) drill-downs at the exact same current-state + actor filter. They never
// route the combined count to the Requirements page alone.
function decisionDialog(
  key: keyof typeof DECISION_CARD_METRICS,
  title: string,
  description: string
): (d: AnyData) => StatCardDef['dialog'] {
  return (d) => ({
    title,
    description,
    links: [
      { label: 'Departmental Labour-Requirement', to: metricTo(DECISION_CARD_METRICS[key].req), value: safeNum(d?.requirements) },
      { label: 'Departmental Labour -Actual', to: metricTo(DECISION_CARD_METRICS[key].act), value: safeNum(d?.actuals) },
    ],
  });
}

function stat(key: string, cfg: StatConfig): DashboardStatRenderer {
  return {
    variant: 'stat',
    stat: (d, urls, admin) => {
      const to = cfg.to?.(urls, admin);
      const dialog = cfg.dialog?.(d);
      return {
        label: label(key),
        value: cfg.value ? cfg.value(d) : num(d),
        icon: cfg.icon,
        color: 'bg-blue-50',
        accent: cfg.accent,
        ...(to ? { to } : {}),
        ...(dialog ? { dialog } : {}),
      };
    },
  };
}

export const DASHBOARD_WIDGET_RENDERERS: Record<string, DashboardWidgetRenderer> = {
  // ── Summary (ORG_ADMIN overview) ────────────────────────────────────────
  projects_total: stat('projects_total', {
    icon: <FolderOpen size={20} />,
    accent: 'blue',
    to: () => '/admin/projects',
  }),
  projects_active: stat('projects_active', {
    icon: <TrendingUp size={20} />,
    accent: 'emerald',
    to: () => '/admin/projects',
  }),
  requirements_total: stat('requirements_total', {
    icon: <ClipboardList size={20} />,
    accent: 'blue',
    to: () => metricTo('requirements_total'),
  }),
  actuals_total: stat('actuals_total', {
    icon: <ClipboardList size={20} />,
    accent: 'blue',
    to: () => metricTo('actuals_total'),
  }),
  actuals_approved_cost: stat('actuals_approved_cost', {
    icon: <IndianRupee size={20} />,
    accent: 'brand',
    value: cur,
    to: () => metricTo('actuals_approved_cost'),
  }),
  users_active: stat('users_active', {
    icon: <Users size={20} />,
    accent: 'blue',
    to: () => '/admin/users',
  }),

  // ── Read-only / shared approved summaries ───────────────────────────────
  requirements_approved: stat('requirements_approved', {
    icon: <FileText size={20} />,
    accent: 'emerald',
    to: () => metricTo('requirements_approved'),
  }),
  actuals_approved: stat('actuals_approved', {
    icon: <FileText size={20} />,
    accent: 'emerald',
    to: () => metricTo('actuals_approved'),
  }),

  // ── Author: requirements (created_by current user) ─────────────────────
  my_draft_requirements: stat('my_draft_requirements', {
    icon: <Clock size={20} />,
    accent: 'amber',
    to: () => metricTo('my_draft_requirements'),
  }),
  my_action_required_requirements: stat('my_action_required_requirements', {
    icon: <AlertTriangle size={20} />,
    accent: 'red',
    to: () => metricTo('my_action_required_requirements'),
  }),

  // ── Author: actual labour (created_by current user) ────────────────────
  my_draft_actuals: stat('my_draft_actuals', {
    icon: <Clock size={20} />,
    accent: 'amber',
    to: () => metricTo('my_draft_actuals'),
  }),
  my_action_required_actuals: stat('my_action_required_actuals', {
    icon: <AlertTriangle size={20} />,
    accent: 'red',
    to: () => metricTo('my_action_required_actuals'),
  }),

  // ── Site Engineer six-card status summary (created_by current user) ─────
  // Totals drill straight into the shared filtered lists (exact owner scope).
  site_total_requirements: stat('site_total_requirements', {
    icon: <FileText size={20} />,
    accent: 'blue',
    to: () => metricTo('site_total_req'),
  }),
  site_total_actuals: stat('site_total_actuals', {
    icon: <HardHat size={20} />,
    accent: 'blue',
    to: () => metricTo('site_total_act'),
  }),
  // Combined cards open a popover with per-entity (Requirements / Actual
  // Labour) drill-downs at the exact same status + owner filter. They never
  // route the combined count to the Requirements page alone.
  site_pending_pm: stat('site_pending_pm', {
    icon: <Clock size={20} />,
    accent: 'amber',
    dialog: (d) => ({
      title: 'Pending PM Approval',
      description: 'Records you submitted that are awaiting the project manager.',
      links: [
        { label: 'Departmental Labour-Requirement', to: metricTo('site_pending_pm_req'), value: safeNum(d?.requirements) },
        { label: 'Departmental Labour -Actual', to: metricTo('site_pending_pm_act'), value: safeNum(d?.actuals) },
      ],
    }),
  }),
  site_pending_ch: stat('site_pending_ch', {
    icon: <Hourglass size={20} />,
    accent: 'amber',
    dialog: (d) => ({
      title: 'Pending CH Approval',
      description: 'Records you submitted that are awaiting the cluster head.',
      links: [
        { label: 'Departmental Labour-Requirement', to: metricTo('site_pending_ch_req'), value: safeNum(d?.requirements) },
        { label: 'Departmental Labour -Actual', to: metricTo('site_pending_ch_act'), value: safeNum(d?.actuals) },
      ],
    }),
  }),
  site_rejected_pm: stat('site_rejected_pm', {
    icon: <XCircle size={20} />,
    accent: 'red',
    dialog: (d) => ({
      title: 'Rejected by PM',
      description: 'Records returned for correction by the project manager.',
      links: [
        { label: 'Departmental Labour-Requirement', to: metricTo('site_rejected_pm_req'), value: safeNum(d?.requirements) },
        { label: 'Departmental Labour -Actual', to: metricTo('site_rejected_pm_act'), value: safeNum(d?.actuals) },
      ],
    }),
  }),
  site_rejected_ch: stat('site_rejected_ch', {
    icon: <XCircle size={20} />,
    accent: 'red',
    dialog: (d) => ({
      title: 'Rejected by CH',
      description: 'Records returned for correction by the cluster head.',
      links: [
        { label: 'Departmental Labour-Requirement', to: metricTo('site_rejected_ch_req'), value: safeNum(d?.requirements) },
        { label: 'Departmental Labour -Actual', to: metricTo('site_rejected_ch_act'), value: safeNum(d?.actuals) },
      ],
    }),
  }),

  // ── Manager approval (four-card dashboard) ──────────────────────────────
  // Pending cards: warning icon, drill straight into the shared filtered lists
  // (exact current-status + self-exclusion / stage filters). Decision cards
  // open a popover with per-entity drill-downs instead of a link.
  awaiting_manager_requirements: stat('awaiting_manager_requirements', {
    icon: <AlertTriangle size={20} />,
    accent: 'amber',
    to: () => metricTo('awaiting_manager_requirements'),
  }),
  awaiting_manager_actuals: stat('awaiting_manager_actuals', {
    icon: <AlertTriangle size={20} />,
    accent: 'amber',
    to: () => metricTo('awaiting_manager_actuals'),
  }),
  manager_decisions_rejected_by_me: stat('manager_decisions_rejected_by_me', {
    icon: <XCircle size={20} />,
    accent: 'red',
    dialog: decisionDialog('manager_decisions_rejected_by_me', 'Rejected by Me', 'Records currently rejected by you.'),
  }),
  manager_decisions_approved_by_me: stat('manager_decisions_approved_by_me', {
    icon: <CheckCircle2 size={20} />,
    accent: 'emerald',
    dialog: decisionDialog('manager_decisions_approved_by_me', 'Approved by Me', 'Records you approved.'),
  }),

  // ── Cluster head / final approval (four-card dashboard) ─────────────────
  awaiting_cluster_requirements: stat('awaiting_cluster_requirements', {
    icon: <AlertTriangle size={20} />,
    accent: 'amber',
    to: () => metricTo('awaiting_cluster_requirements'),
  }),
  awaiting_cluster_actuals: stat('awaiting_cluster_actuals', {
    icon: <AlertTriangle size={20} />,
    accent: 'amber',
    to: () => metricTo('awaiting_cluster_actuals'),
  }),
  cluster_decisions_rejected_by_me: stat('cluster_decisions_rejected_by_me', {
    icon: <XCircle size={20} />,
    accent: 'red',
    dialog: decisionDialog('cluster_decisions_rejected_by_me', 'Rejected by Me', 'Records currently rejected by you.'),
  }),
  cluster_decisions_approved_by_me: stat('cluster_decisions_approved_by_me', {
    icon: <CheckCircle2 size={20} />,
    accent: 'emerald',
    dialog: decisionDialog('cluster_decisions_approved_by_me', 'Finally Approved by Me', 'Records you finally approved.'),
  }),

  // ── Analytics (Organization Analysis, ORG_ADMIN only) ───────────────────
  requirements_by_status: {
    variant: 'chart',
    render: (d, urls) => <W.RequirementsByStatusCard items={d?.items || []} urls={urls} />,
  },
  planned_vs_actual: {
    variant: 'chart',
    render: (d, urls) => <W.PlannedVsActualCard data={d} urls={urls} />,
  },
  regular_vs_adhoc: {
    variant: 'chart',
    render: (d, urls, admin) => <W.LabourByTypeCard items={d?.items || []} urls={urls} admin={admin} />,
  },
  labour_by_skill: {
    variant: 'chart',
    render: (d, urls, admin) => <W.LabourBySkillCard items={d?.items || []} urls={urls} admin={admin} />,
  },
  cost_by_project: {
    variant: 'chart',
    render: (d, urls) => <W.CostByProjectCard items={d?.items || []} urls={urls} />,
  },

  // ── Read-only recently-approved section (Viewer / read-only roles) ──────
  recently_approved_records: {
    variant: 'list',
    render: (d) => (
      <W.RecentListCard
        title={label('recently_approved_records')}
        emptyText={empty('recently_approved_records')}
        items={d?.items || []}
      />
    ),
  },

  // ── Read-only approved records (ORG_ADMIN Organization Analysis) ────────
  recent_approved_requirements: {
    variant: 'list',
    render: (d, urls) => (
      <W.RequirementListCard
        title={label('recent_approved_requirements')}
        emptyText={empty('recent_approved_requirements')}
        items={d?.items || []}
        linkTo={metricLink('requirements_approved', urls)}
        linkLabel="View all approved"
      />
    ),
  },
  recent_approved_actuals: {
    variant: 'list',
    render: (d, urls) => (
      <W.ActualListCard
        title={label('recent_approved_actuals')}
        emptyText={empty('recent_approved_actuals')}
        items={d?.items || []}
        linkTo={metricLink('actuals_approved', urls)}
        linkLabel="View all approved"
      />
    ),
  },
};

/** Variant lookup for arranging widgets in the shared page layout. */
export function widgetVariant(key: string): DashboardWidgetVariant {
  return DASHBOARD_WIDGET_RENDERERS[key]?.variant ?? 'list';
}

/** Render an authorized widget by key; returns null for unknown keys. */
export function renderWidget(
  key: string,
  data: AnyData,
  urls: W.DashboardUrls,
  admin: boolean
): React.ReactNode | StatCardDef {
  const renderer = DASHBOARD_WIDGET_RENDERERS[key];
  if (!renderer) return null;
  if (renderer.variant === 'stat') return renderer.stat(data, urls, admin);
  return renderer.render(data, urls, admin);
}
